import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { Link } from '@lifeomic/chroma-react/components/Link';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { useMixpanel } from '@lifeomic/phc-web-toolkit/src/hooks/useMixpanel';

import urls from '../../../util/urls';
import { DocumentPreview } from './DocumentPreview';
import { useFileValidationResolutionCommand } from './commandClient';
import type {
  FileValidationResolutionAction,
  FileValidationTarget,
} from './types';

const messages = defineMessages({
  title: {
    id: 'FileValidationResolver.title',
    defaultMessage: 'Resolve file validation alert',
  },
  memberSection: {
    id: 'FileValidationResolver.memberSection',
    defaultMessage: 'Member',
  },
  memberName: {
    id: 'FileValidationResolver.memberName',
    defaultMessage: 'Name',
  },
  dateOfBirth: {
    id: 'FileValidationResolver.dateOfBirth',
    defaultMessage: 'Date of birth',
  },
  documentSection: {
    id: 'FileValidationResolver.documentSection',
    defaultMessage: 'Document',
  },
  fileName: {
    id: 'FileValidationResolver.fileName',
    defaultMessage: 'File',
  },
  fileType: {
    id: 'FileValidationResolver.fileType',
    defaultMessage: 'Type',
  },
  indexed: {
    id: 'FileValidationResolver.indexed',
    defaultMessage: 'Indexed',
  },
  gender: {
    id: 'FileValidationResolver.gender',
    defaultMessage: 'Gender',
  },
  elationId: {
    id: 'FileValidationResolver.elationId',
    defaultMessage: 'Elation ID',
  },
  missing: {
    id: 'FileValidationResolver.missing',
    defaultMessage: '<missing>',
  },
  zoriEvidence: {
    id: 'FileValidationResolver.zoriEvidence',
    defaultMessage: 'Zori evidence',
  },
  invalidReason: {
    id: 'FileValidationResolver.invalidReason',
    defaultMessage: '{reason}',
  },
  invalidReasonUnavailable: {
    id: 'FileValidationResolver.invalidReasonUnavailable',
    defaultMessage: 'Zori marked this file as invalid.',
  },
  reviewGuidance: {
    id: 'FileValidationResolver.reviewGuidance',
    defaultMessage:
      'Review the document and choose the outcome that matches the member chart.',
  },
  deleteAction: {
    id: 'FileValidationResolver.deleteAction',
    defaultMessage: 'Delete',
  },
  deleteFraming: {
    id: 'FileValidationResolver.deleteFraming',
    defaultMessage: 'Removes chart data for this member.',
  },
  overrideAction: {
    id: 'FileValidationResolver.overrideAction',
    defaultMessage: 'Override validation',
  },
  overrideFraming: {
    id: 'FileValidationResolver.overrideFraming',
    defaultMessage: 'Confirms this file belongs on this member chart.',
  },
  close: {
    id: 'FileValidationResolver.close',
    defaultMessage: 'Close',
  },
  deleteConfirmTitle: {
    id: 'FileValidationResolver.deleteConfirmTitle',
    defaultMessage: 'Delete file from chart?',
  },
  deleteConfirmBody: {
    id: 'FileValidationResolver.deleteConfirmBody',
    defaultMessage:
      'This deletes chart data for this member. Continue only if the file does not belong on this chart.',
  },
  overrideConfirmTitle: {
    id: 'FileValidationResolver.overrideConfirmTitle',
    defaultMessage: 'Confirm file belongs on this chart?',
  },
  overrideConfirmBody: {
    id: 'FileValidationResolver.overrideConfirmBody',
    defaultMessage:
      'This confirms the file belongs on this member chart and should be treated as valid.',
  },
  cancel: {
    id: 'FileValidationResolver.cancel',
    defaultMessage: 'Cancel',
  },
  confirm: {
    id: 'FileValidationResolver.confirm',
    defaultMessage: 'Confirm',
  },
  confirmingUpdate: {
    id: 'FileValidationResolver.confirmingUpdate',
    defaultMessage: 'Confirming the document update…',
  },
  confirmedUpdate: {
    id: 'FileValidationResolver.confirmedUpdate',
    defaultMessage: 'Document update confirmed.',
  },
  timeoutUpdate: {
    id: 'FileValidationResolver.timeoutUpdate',
    defaultMessage:
      'This is taking longer than expected. Refresh and check again. If it still has not updated, contact #tech-support.',
  },
  errorUpdate: {
    id: 'FileValidationResolver.errorUpdate',
    defaultMessage:
      'Unable to submit the document update. Please try again or contact #tech-support.',
  },
  confirmationError: {
    id: 'FileValidationResolver.confirmationError',
    defaultMessage:
      'The update was submitted, but PHC could not confirm the document changed. Refresh and check again. If it still has not updated, contact #tech-support.',
  },
});

const useStyles = makeStyles(theme => ({
  content: {
    display: 'grid',
    gridTemplateColumns: `${theme.pxToRem(380)} minmax(0, 1fr)`,
    gap: theme.spacing(3),
    height: 'calc(100vh - 150px)',
    minHeight: theme.pxToRem(620),
  },
  leftPane: {
    overflowY: 'auto',
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: theme.spacing(3),
  },
  rightPane: {
    minWidth: 0,
    minHeight: 0,
    width: '100%',
  },
  evidence: {
    border: `1px solid #C77700`,
    borderRadius: theme.shape.borderRadius,
    background: '#FFF7E8',
    borderLeftWidth: theme.pxToRem(4),
  },
  evidenceTitle: {
    color: '#8A4B00',
  },
  evidenceReason: {
    color: '#5F3400',
    fontStyle: 'italic',
    lineHeight: 1.5,
  },
  detailRow: {
    display: 'grid',
    gridTemplateColumns: `${theme.pxToRem(112)} minmax(0, 1fr)`,
    gap: theme.spacing(1),
    alignItems: 'baseline',
  },
  label: {
    color: theme.palette.text.hint,
    fontWeight: theme.typography.fontWeightBold,
  },
  cautionButton: {
    borderColor: '#C77700',
    color: '#8A4B00',
    '&&:hover, &&:focus': {
      borderColor: '#8A4B00',
      backgroundColor: '#FFF7E8',
      color: '#8A4B00',
    },
  },
  cautionConfirmButton: {
    backgroundColor: '#C77700',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#8A4B00',
    },
  },
  status: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  actionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
  },
  actionCard: {
    minWidth: 0,
  },
}));

const DetailRow: React.FC<{
  label: string;
  value?: React.ReactNode;
}> = ({ label, value }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  return (
    <div className={classes.detailRow}>
      <Text size="caption" className={classes.label}>
        {label}
      </Text>
      <Text size="subbody">{value || formatMessage(messages.missing)}</Text>
    </div>
  );
};

const formatHumanDateTime = (
  value: string | undefined,
  formatDate: ReturnType<typeof useIntl>['formatDate'],
) => {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const FileValidationResolver: React.FC<{
  isOpen: boolean;
  target: FileValidationTarget | null;
  onDismiss: () => void;
  onResolutionConfirmed?: (
    target: FileValidationTarget,
    action: FileValidationResolutionAction,
  ) => void;
}> = ({ isOpen, target, onDismiss, onResolutionConfirmed }) => {
  const classes = useStyles();
  const { formatDate, formatMessage } = useIntl();
  const { track } = useMixpanel();
  const [pendingAction, setPendingAction] =
    React.useState<FileValidationResolutionAction | null>(null);
  const activeResolutionId = React.useRef(0);
  const documentReference = target?.documentReference;
  const documentTitle =
    documentReference?.description ||
    documentReference?.type?.text ||
    documentReference?.content?.[0]?.attachment?.title;
  const commandMutation = useFileValidationResolutionCommand();
  const {
    isResolved,
    isResolving,
    mutateAsync: resolveFileValidation,
    reset: resetResolutionCommand,
    resolutionStatus,
  } = commandMutation;
  const memberChartUrl = target
    ? `/phc${urls.app.members(target.patientId)}`
    : '';

  React.useEffect(() => {
    if (!isOpen) {
      activeResolutionId.current += 1;
      setPendingAction(null);
      resetResolutionCommand();
    }
  }, [isOpen, resetResolutionCommand]);

  const runResolution = React.useCallback(async () => {
    if (!target || !pendingAction || isResolving) {
      return;
    }

    const action = pendingAction;
    const resolutionId = activeResolutionId.current + 1;

    activeResolutionId.current = resolutionId;
    setPendingAction(null);

    let confirmation;
    try {
      confirmation = await resolveFileValidation({
        patientId: target.patientId,
        documentReferenceId: target.documentReferenceId,
        action,
      });
    } catch {
      track('file_validation_resolution_completed', {
        action: action === 'mark-valid' ? 'mark_valid' : 'delete',
        outcome: 'submission_error',
      });
      return;
    }

    track('file_validation_resolution_completed', {
      action: action === 'mark-valid' ? 'mark_valid' : 'delete',
      outcome:
        confirmation.status === 'confirmed'
          ? 'confirmed'
          : confirmation.status === 'timeout'
          ? 'timeout'
          : 'confirmation_error',
    });

    if (
      confirmation.status === 'confirmed' &&
      activeResolutionId.current === resolutionId
    ) {
      onResolutionConfirmed?.(target, action);
    }
  }, [
    isResolving,
    onResolutionConfirmed,
    pendingAction,
    resolveFileValidation,
    target,
    track,
  ]);

  const statusMessage =
    resolutionStatus === 'confirming'
      ? formatMessage(messages.confirmingUpdate)
      : resolutionStatus === 'confirmed'
      ? formatMessage(messages.confirmedUpdate)
      : resolutionStatus === 'timeout'
      ? formatMessage(messages.timeoutUpdate)
      : resolutionStatus === 'confirmation-error'
      ? formatMessage(messages.confirmationError)
      : resolutionStatus === 'error'
      ? formatMessage(messages.errorUpdate)
      : undefined;

  const pendingActionTitle =
    pendingAction === 'delete'
      ? formatMessage(messages.deleteConfirmTitle)
      : pendingAction === 'mark-valid'
      ? formatMessage(messages.overrideConfirmTitle)
      : '';

  const pendingActionBody =
    pendingAction === 'delete'
      ? formatMessage(messages.deleteConfirmBody)
      : pendingAction === 'mark-valid'
      ? formatMessage(messages.overrideConfirmBody)
      : '';
  const invalidReasonMessage = target?.invalidReason?.display
    ? formatMessage(messages.invalidReason, {
        reason: target.invalidReason.display,
      })
    : formatMessage(messages.invalidReasonUnavailable);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onDismiss={isResolving ? () => undefined : onDismiss}
        title={formatMessage(messages.title)}
        isFullScreen
        actions={
          <Button variant="text" onClick={onDismiss} disabled={isResolving}>
            {formatMessage(messages.close)}
          </Button>
        }
      >
        {target && documentReference ? (
          <div className={classes.content}>
            <Box className={classes.leftPane} direction="column" gap={2}>
              <Text size="headline">
                {formatMessage(messages.memberSection)}
              </Text>
              <DetailRow
                label={formatMessage(messages.memberName)}
                value={
                  <Link to={memberChartUrl} newTab>
                    {target.member.name}
                  </Link>
                }
              />
              <DetailRow
                label={formatMessage(messages.elationId)}
                value={
                  target.member.elationId && target.member.elationChartUrl ? (
                    <Link to={target.member.elationChartUrl} newTab>
                      {target.member.elationId}
                    </Link>
                  ) : (
                    target.member.elationId
                  )
                }
              />
              <DetailRow
                label={formatMessage(messages.dateOfBirth)}
                value={target.member.dateOfBirth}
              />
              <DetailRow
                label={formatMessage(messages.gender)}
                value={target.member.gender}
              />

              <Divider />

              <Text size="headline">
                {formatMessage(messages.documentSection)}
              </Text>
              <DetailRow
                label={formatMessage(messages.fileName)}
                value={documentTitle}
              />
              <DetailRow
                label={formatMessage(messages.fileType)}
                value={documentReference.type?.text}
              />
              <DetailRow
                label={formatMessage(messages.indexed)}
                value={formatHumanDateTime(
                  documentReference.indexed,
                  formatDate,
                )}
              />

              <Box
                className={classes.evidence}
                direction="column"
                gap={1}
                padding={2}
              >
                <Text className={classes.evidenceTitle} size="headline">
                  {formatMessage(messages.zoriEvidence)}
                </Text>
                <Text className={classes.evidenceReason} size="subbody">
                  “{invalidReasonMessage}”
                </Text>
              </Box>

              {statusMessage ? (
                <Box
                  className={classes.status}
                  direction="column"
                  gap={1}
                  padding={2}
                >
                  <Text size="subbody">{statusMessage}</Text>
                </Box>
              ) : null}

              <Text size="subbody">
                {formatMessage(messages.reviewGuidance)}
              </Text>

              <div className={classes.actionGrid}>
                <Box
                  className={classes.actionCard}
                  direction="column"
                  gap={0.5}
                >
                  <Button
                    color="negative"
                    variant="outlined"
                    onClick={() => setPendingAction('delete')}
                    fullWidth
                    disabled={isResolving || isResolved}
                  >
                    {formatMessage(messages.deleteAction)}
                  </Button>
                  <Text size="caption">
                    {formatMessage(messages.deleteFraming)}
                  </Text>
                </Box>

                <Box
                  className={classes.actionCard}
                  direction="column"
                  gap={0.5}
                >
                  <Button
                    className={classes.cautionButton}
                    variant="outlined"
                    onClick={() => setPendingAction('mark-valid')}
                    fullWidth
                    disabled={isResolving || isResolved}
                  >
                    {formatMessage(messages.overrideAction)}
                  </Button>
                  <Text size="caption">
                    {formatMessage(messages.overrideFraming)}
                  </Text>
                </Box>
              </div>
            </Box>

            <Box className={classes.rightPane}>
              <DocumentPreview documentReference={documentReference} />
            </Box>
          </div>
        ) : null}
      </Modal>
      <Modal
        isOpen={!!pendingAction}
        onDismiss={() => setPendingAction(null)}
        title={pendingActionTitle}
        actions={
          <>
            <Button variant="text" onClick={() => setPendingAction(null)}>
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              color={pendingAction === 'delete' ? 'negative' : undefined}
              className={
                pendingAction === 'mark-valid'
                  ? classes.cautionConfirmButton
                  : undefined
              }
              onClick={runResolution}
              disabled={isResolving}
            >
              {formatMessage(messages.confirm)}
            </Button>
          </>
        }
      >
        <Text size="subbody">{pendingActionBody}</Text>
      </Modal>
    </>
  );
};
